import React, { useState, useEffect, useRef } from 'react';
import classNames from 'classnames/bind';
import { faComment } from '@fortawesome/free-solid-svg-icons/faComment';
import moment from 'moment';
import { formatNumber } from 'humanize-plus';
import { titleize } from 'inflection';
import { scroller } from 'react-scroll';

import Button, { BUTTON_TYPES, BUTTON_SIZES } from 'buyside/components/presentation/Button/button';
import IdeaUpdateComments from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateComments/ideaUpdateComments';
import IdeaUpdateItem from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateCard/IdeaUpdateItem/ideaUpdateItem';
import IdeaUpdateModal from 'buyside/components/presentation/IdeaShowPage/AuthorActions/ideaUpdateModal';
import Link from 'buyside/components/presentation/Link/link';
import ParsedHtml from 'buyside/components/presentation/ParsedHtml/parsedHtml';
import YouTubePlayer from 'buyside/components/presentation/YouTubePlayer/youtubePlayer';

import BlockedIcon from 'buyside/svgs/blocked.svg';
import ScaleIcon from 'buyside/svgs/scale.svg';
import ThumbsDownIcon from 'buyside/svgs/thumbs_down.svg';
import ThumbsUpIcon from 'buyside/svgs/thumbs_up.svg';

import { isEmpty } from 'buyside/util/lodashAlt';
import { adminEditIdeaUpdate } from 'buyside/util/urlHelper';
import findEventId, { EventType } from 'buyside/util/parseLocationHash';

import styles from 'buyside/components/presentation/IdeaUpdates/IdeaUpdateCard/ideaUpdateCardStyles.scss';

const IdeaUpdateCard = ({
  authorId,
  authorName,
  canAccessComments,
  canCurrentUserViewIdeaAttachments,
  canWriteComments,
  canViewUserDetails,
  currentUserId,
  ideaId,
  ideaUpdate,
  isAdmin,
  isAuthor,
  isCurrencySubunit,
  isIdeaAnonymous,
  isNonBuyside
}) => {
  const [truncateText, setTruncateText] = useState(true);
  const [showComments, setShowComments] = useState(false);

  const {
    addedCatalystNames,
    body,
    closed,
    commentsCount,
    createdAt,
    id: ideaUpdateId,
    ideaAttachmentsArray = [],
    impact: ideaUpdateImpact,
    priceTarget,
    removedCatalystNames,
    removedPriceTarget,
    removedReturnTarget,
    removedSituation,
    removedTargetAllocation,
    removedTimeframe,
    returnTarget,
    situation,
    targetAllocation,
    timeframe,
    truncated,
    youtubeIds
  } = ideaUpdate;

  const hash = window.location.hash.slice(1);
  const updateId = findEventId(hash, EventType.Update);
  const commentId = findEventId(hash, EventType.Comment);

  const divId = `ideaupdate-${ideaUpdateId}`;
  const commentsDivId = divId + '-comments';
  const postCommentDivId = commentsDivId + '-create';

  const highlightedCommentId = useRef(commentId);

  useEffect(() => {
    if (updateId && updateId === ideaUpdateId) {
      setShowComments(true);

      scroller.scrollTo(divId, {
        duration: 1000,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
  }, [updateId, divId, ideaUpdateId]);

  const toggleTruncation = () => {
    if (!truncateText) {
      scroller.scrollTo(divId, {
        duration: 500,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
    setTruncateText(!truncateText);
  };

  const toggleComments = () => {
    if (!commentsCount && !canWriteComments) return;

    if (!showComments) {
      scroller.scrollTo(commentsDivId, {
        duration: 500,
        delay: 0,
        smooth: 'easeInOutQuint',
        offset: -(65 + 45 + 15) // -(top navigation height + page navigator height + some padding)
      });
    }
    setShowComments(!showComments);
  };

  let impact = ideaUpdateImpact;
  if (!impact) impact = 'neutral';

  let impactIcon = null;

  if (closed) {
    impactIcon = <BlockedIcon className={styles.blockedIcon} />;
  } else if (impact === 'negative') {
    impactIcon = <ThumbsDownIcon className={styles.thumbsDownIcon} />;
  } else if (impact === 'positive') {
    impactIcon = <ThumbsUpIcon className={styles.thumbsUpIcon} />;
  } else if (impact === 'neutral') {
    impactIcon = <ScaleIcon className={styles.scaleIcon} />;
  }

  const attachments = ideaAttachmentsArray || [];

  return (
    <div className={styles.ideaUpdateCard} id={divId}>
      <div className={styles.leftRow}>
        <div className={styles.dateDisplay}>{moment(createdAt).format('M/D/YY')}</div>
        <div>{impactIcon}</div>
        {(closed && <div className={styles.negativeUpdate}>Closed Position</div>) ||
          (impact && (
            <div
              className={classNames({
                [styles.negativeUpdate]: impact === 'negative',
                [styles.positiveUpdate]: impact === 'positive',
                [styles.neutralUpdate]: impact === 'neutral'
              })}
            >
              {titleize(impact)} Update
            </div>
          ))}
      </div>
      <div className={styles.rightRow}>
        <div className={styles.updateItemSection}>
          {removedTargetAllocation ? (
            <IdeaUpdateItem
              item={{
                added: targetAllocation,
                removed: removedTargetAllocation
              }}
              type="targetAllocation"
              showAttachments={false}
            />
          ) : null}
          {removedPriceTarget ? (
            <IdeaUpdateItem
              item={{ added: priceTarget, removed: removedPriceTarget }}
              type="priceTarget"
              isCurrencySubunit={isCurrencySubunit}
              showAttachments={false}
            />
          ) : null}
          {removedReturnTarget ? (
            <IdeaUpdateItem
              item={{ added: returnTarget, removed: removedReturnTarget }}
              type="returnTarget"
              showAttachments={false}
            />
          ) : null}
          {removedSituation ? (
            <IdeaUpdateItem
              item={{ added: situation, removed: removedSituation }}
              type="situation"
              showAttachments={false}
            />
          ) : null}
          {removedTimeframe ? (
            <IdeaUpdateItem
              item={{ added: timeframe, removed: removedTimeframe }}
              type="timeframe"
              showAttachments={false}
            />
          ) : null}
          {!isEmpty(removedCatalystNames) || !isEmpty(addedCatalystNames) ? (
            <IdeaUpdateItem
              item={{
                added: addedCatalystNames,
                removed: removedCatalystNames
              }}
              type="catalyst"
              showAttachments={false}
            />
          ) : null}
          {attachments.map((ideaAttachment, idx) => (
            <IdeaUpdateItem
              key={`${idx}-attachment`}
              item={ideaAttachment}
              type="attachment"
              showAttachments={canCurrentUserViewIdeaAttachments}
            />
          ))}
        </div>
        <div className={classNames({ [styles.truncated]: truncated && truncateText })}>
          {body && (
            <ParsedHtml html={body}>
              {({ content, tinyMceStyles }) => (
                <div className={classNames(tinyMceStyles, styles.ideaUpdateBody)}>{content}</div>
              )}
            </ParsedHtml>
          )}
          {closed && !body && (
            <div className={styles.updateBodyClosed}>
              The author has recommended exiting the position on{' '}
              {moment(createdAt).format('MMM DD, YYYY')}
            </div>
          )}
          {truncated ? (
            (truncateText && (
              <div className={styles.truncationClickArea}>
                <div className={styles.clickAreaBackground} />
                <div className={styles.toggleTruncateContainer}>
                  <div className={styles.readMoreButton}>
                    <div onClick={toggleTruncation}>Read More</div>
                  </div>
                  <Button
                    disabled={!canAccessComments}
                    onClick={toggleComments}
                    text={`Comments (${commentsCount || 0})`}
                    faIcon={faComment}
                    className={styles.readMoreCommentsButton}
                    type={BUTTON_TYPES.LINK}
                    size={BUTTON_SIZES.SMALL}
                  />
                </div>
              </div>
            )) || (
              <div className={styles.toggleTruncateContainer}>
                <span className={styles.readLessButton} onClick={toggleTruncation}>
                  Read Less
                </span>
                <Button
                  disabled={!canAccessComments}
                  onClick={toggleComments}
                  text={`Comments (${commentsCount || 0})`}
                  faIcon={faComment}
                  type={BUTTON_TYPES.LINK}
                  size={BUTTON_SIZES.SMALL}
                />
              </div>
            )
          ) : (
            <div className={styles.postCommentContainer}>
              <Button
                disabled={!canAccessComments}
                onClick={toggleComments}
                text={`Comments (${commentsCount || 0})`}
                faIcon={faComment}
                type={BUTTON_TYPES.LINK}
                size={BUTTON_SIZES.SMALL}
              />
            </div>
          )}
          {youtubeIds &&
            youtubeIds.map(id => (
              <YouTubePlayer key={id} width={489.6} height={298.35} videoId={id} />
            ))}
        </div>
        {(isAuthor || isAdmin) &&
          !closed &&
          moment().diff(moment(new Date(createdAt)), 'hours') < 24 && (
            <section className={styles.editUpdateSection}>
              <IdeaUpdateModal
                analyticsTag="edit-update"
                attachments={attachments}
                body={body}
                catalysts={ideaUpdate.catalystNames}
                closeIdea={closed}
                createdAt={createdAt}
                ideaId={ideaId}
                ideaUpdateId={ideaUpdateId}
                impact={impact}
                modalText="Edit Update"
                priceTarget={
                  Boolean(priceTarget) || priceTarget === 0
                    ? formatNumber(priceTarget || 0, 2)
                    : null
                }
                returnTarget={returnTarget ? formatNumber(returnTarget, 2) : null}
                situation={situation}
                targetAllocation={targetAllocation}
                timeframe={timeframe}
              />
              <em>
                <strong>Note:</strong> You can only edit this update within a 24 hour period.
              </em>
            </section>
          )}
        {isAdmin && (
          <div className={styles.adminLink}>
            <Link to={adminEditIdeaUpdate(ideaId, ideaUpdateId)} target="_blank">
              Edit In Admin
            </Link>
          </div>
        )}
        <div id={commentsDivId}>
          {showComments && canAccessComments && (
            <IdeaUpdateComments
              authorId={authorId}
              authorName={authorName}
              canWriteComments={canWriteComments}
              canViewUserDetails={canViewUserDetails}
              currentUserId={currentUserId}
              highlightedCommentId={highlightedCommentId.current}
              ideaUpdateId={ideaUpdateId}
              isAdmin={isAdmin}
              isAuthor={isAuthor}
              isIdeaAnonymous={isIdeaAnonymous}
              isNonBuyside={isNonBuyside}
              postCommentDivId={postCommentDivId}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IdeaUpdateCard;
